import React, { useState, useCallback, useEffect } from 'react';

const StepNationalite = ({ formData, onChange }) => {
  // État local pour la gestion des erreurs
  const [localErrors, setLocalErrors] = useState({});

  // Fonction de validation des champs avec useCallback pour prévenir les re-créations inutiles
  const validateFields = useCallback(() => {
    const errors = {};
    
    // Vérifier la nationalité
    if (!formData.nationalite) {
      errors.nationalite = 'Veuillez sélectionner votre nationalité';
    } else if (formData.nationalite === 'Autre' && !formData.nationaliteAutre) {
      errors.nationaliteAutre = 'Veuillez préciser votre nationalité';
    }
    
    // Vérifier le pays de naissance
    if (!formData.paysNaissance) {
      errors.paysNaissance = 'Veuillez sélectionner votre pays de naissance';
    } else if (formData.paysNaissance === 'Autre') {
      if (!formData.paysNaissanceAutre) {
        errors.paysNaissanceAutre = 'Veuillez préciser votre pays de naissance';
      }
      if (!formData.villeNaissanceAutre) {
        errors.villeNaissanceAutre = 'Veuillez préciser votre ville de naissance';
      }
    } else if (formData.paysNaissance === 'France' && !formData.villeNaissance) {
      // Ajout de la validation pour la ville de naissance en France
      errors.villeNaissance = 'Veuillez préciser votre ville de naissance en France';
    }
    
    setLocalErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  // Gestionnaire pour le changement des champs
  const handleChange = (e) => {
    const { name, value } = e.target;
    onChange({ target: { name, value } });
    
    // Effacer l'erreur pour ce champ
    if (localErrors[name]) {
      setLocalErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };
  
  // Gestionnaire pour le clic sur les boutons
  const handleButtonClick = (fieldName, value) => {
    onChange({ target: { name: fieldName, value } });
    
    // Effacer l'erreur pour ce champ
    if (localErrors[fieldName]) {
      setLocalErrors(prev => {
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });
    }
    
    // Si on change de pays de naissance, réinitialiser les champs liés
    if (fieldName === 'paysNaissance') {
      if (value === 'France') {
        onChange({ target: { name: 'paysNaissanceAutre', value: '' } });
        onChange({ target: { name: 'villeNaissanceAutre', value: '' } });
      } else {
        onChange({ target: { name: 'villeNaissance', value: '' } });
      }
    }
  };

  // Intercepter le clic sur le bouton suivant avec validateFields comme dépendance
  useEffect(() => {
    const nextButton = document.querySelector('button.bg-orpi-primary:not(.mt-8)');
    if (nextButton) {
      const originalOnClick = nextButton.onclick;
      
      nextButton.onclick = (e) => {
        const isValid = validateFields();
        if (!isValid) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
        
        // Si valide, continuer avec le comportement normal
        if (originalOnClick) {
          return originalOnClick(e);
        }
      };
      
      // Nettoyage
      return () => {
        if (nextButton) {
          nextButton.onclick = originalOnClick;
        }
      };
    }
  }, [validateFields]); // Ajout de validateFields dans les dépendances

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-orpi-secondary">
        Quelle est votre nationalité ?
      </h2>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-1 text-orpi-secondary">
            Nationalité <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              className={`p-3 border rounded-lg hover:border-orpi-primary ${
                formData.nationalite === 'Française' ? 'border-orpi-primary bg-orpi-light' : ''
              }`}
              onClick={() => handleButtonClick('nationalite', 'Française')}
            >
              Française
            </button>
            <button
              type="button"
              className={`p-3 border rounded-lg hover:border-orpi-primary ${
                formData.nationalite === 'Autre' ? 'border-orpi-primary bg-orpi-light' : ''
              }`}
              onClick={() => handleButtonClick('nationalite', 'Autre')}
            >
              Autre
            </button>
          </div>
          {localErrors.nationalite && (
            <p className="mt-1 text-sm text-red-600">{localErrors.nationalite}</p>
          )}
        </div>

        {formData.nationalite === 'Autre' && (
          <div>
            <label className="block text-sm font-medium mb-1 text-orpi-secondary">
              Précisez votre nationalité <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="nationaliteAutre"
              value={formData.nationaliteAutre || ''}
              onChange={handleChange}
              className={`w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary ${
                localErrors.nationaliteAutre ? 'border-red-500' : ''
              }`}
              placeholder="Votre nationalité"
              required
            />
            {localErrors.nationaliteAutre && (
              <p className="mt-1 text-sm text-red-600">{localErrors.nationaliteAutre}</p>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-1 text-orpi-secondary">
            Pays de naissance <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              className={`p-3 border rounded-lg hover:border-orpi-primary ${
                formData.paysNaissance === 'France' ? 'border-orpi-primary bg-orpi-light' : ''
              }`}
              onClick={() => handleButtonClick('paysNaissance', 'France')}
            >
              France
            </button>
            <button
              type="button"
              className={`p-3 border rounded-lg hover:border-orpi-primary ${
                formData.paysNaissance === 'Autre' ? 'border-orpi-primary bg-orpi-light' : ''
              }`}
              onClick={() => handleButtonClick('paysNaissance', 'Autre')}
            >
              Autre
            </button>
          </div>
          {localErrors.paysNaissance && (
            <p className="mt-1 text-sm text-red-600">{localErrors.paysNaissance}</p>
          )}
        </div>

        {formData.paysNaissance === 'France' && (
          <div>
            <label className="block text-sm font-medium mb-1 text-orpi-secondary">
              Ville de naissance en France <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="villeNaissance"
              value={formData.villeNaissance || ''}
              onChange={handleChange}
              className={`w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary ${
                localErrors.villeNaissance ? 'border-red-500' : ''
              }`}
              placeholder="Votre ville de naissance"
              required
            />
            {localErrors.villeNaissance && (
              <p className="mt-1 text-sm text-red-600">{localErrors.villeNaissance}</p>
            )}
          </div>
        )}

        {formData.paysNaissance === 'Autre' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-orpi-secondary">
                Précisez votre pays de naissance <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="paysNaissanceAutre"
                value={formData.paysNaissanceAutre || ''}
                onChange={handleChange}
                className={`w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary ${
                  localErrors.paysNaissanceAutre ? 'border-red-500' : ''
                }`}
                placeholder="Votre pays de naissance"
                required
              />
              {localErrors.paysNaissanceAutre && (
                <p className="mt-1 text-sm text-red-600">{localErrors.paysNaissanceAutre}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-orpi-secondary">
                Ville de naissance <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="villeNaissanceAutre"
                value={formData.villeNaissanceAutre || ''}
                onChange={handleChange}
                className={`w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary ${
                  localErrors.villeNaissanceAutre ? 'border-red-500' : ''
                }`}
                placeholder="Votre ville de naissance"
                required
              />
              {localErrors.villeNaissanceAutre && (
                <p className="mt-1 text-sm text-red-600">{localErrors.villeNaissanceAutre}</p>
              )}
            </div>
          </div>
        )}
        
        <p className="text-sm text-gray-500 mt-4">
          <span className="text-red-500">*</span> Champs obligatoires
        </p>
      </div>
    </div>
  );
};

export default StepNationalite;